import React, { Component } from 'react';
import { Confirm } from 'semantic-ui-react';
import './delete-cofirmation-modal.css';
import { NARRATIVEBOARDCONFIG } from '../narrative-board/narrative-board-config';

interface IProps {
    open: boolean;
    close: (is_delete: boolean) => void;
    content:string;
}

/**
 * A confirmation modal that prompts the user before deleting an item.
 *
 * This component wraps the Semantic UI `Confirm` component with custom styling
 * and localized button labels from {@link NARRATIVEBOARDCONFIG}.
 *
 * @remarks
 * - Opens when the `open` prop is `true`.
 * - Displays the `content` prop as the confirmation message.
 * - Calls the `close` callback with `true` (Yes) or `false` (No).
 * - Cannot be dismissed with Escape, clicking the dimmer, or clicking outside.
 *
 * @example
 * ```tsx
 * <DeleteConfirmationModal
 *   open={isOpen}
 *   content="Are you sure you want to delete this record?"
 *   close={(confirmed) => {
 *     if (confirmed) {
 *       handleDelete();
 *     }
 *   }}
 * />
 * ```
 */
export default class DeleteConfirmationModal extends Component<IProps>{
    /**
     * Handles closing the modal by invoking the parent `close` callback.
     *
     * @param is_delete - `true` if deletion is confirmed, `false` if canceled.
     */
    close = (is_delete : boolean) => {
        this.props.close(is_delete);
    }
    
    /**
     * Renders the delete confirmation dialog using Semantic UI's `Confirm`.
     *
     * The dialog includes:
     * - A message (`content` prop).
     * - "Yes" and "No" buttons with localized labels.
     * - No ability to close with Escape, dimmer, or outside clicks.
     */
    render() {
        return (
            <Confirm open={this.props.open} className='delete-modal'
                content={this.props.content} size='mini'
                cancelButton={{ content: NARRATIVEBOARDCONFIG.ACTION.NO, negative: true }}
                confirmButton={{ content: NARRATIVEBOARDCONFIG.ACTION.YES, positive: true }}
                closeOnEscape={false}
                closeOnDimmerClick={false}
                closeOnDocumentClick={false}
                onCancel={()=>this.close(false)}
                onConfirm={()=>this.close(true)} />
        )
    }
}